import React, { useState } from "react";
import "./Orders.css";
import { OverlayTrigger,Tooltip } from "react-bootstrap";
import {  Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOrders } from "../../customHooks/Orders/useOrders";
import AppLoader from "../../components/AppLoader/appLoader";
import {formatLabel } from "../../components/Constants/Constants";
import Pagination from "../../components/Pagination/Pagination";
import CopyIcon from "../../components/CopyIcon/CopyIcon";
import UnAuthorized from "../UnAuthorized/UnAuthorized";

// Define column information for tooltips
const columnInfo = {
  buyerOrderId: "The unique identifier for the order generated by the Buyer App.",
  sellerOrderId: "The unique identifier for the order generated by the Seller App.",
  txnId: "The unique transaction ID associated with the order.",
  totalOrderAmount: "The total amount of the order, including all taxes and fees.",
  orderStatus: "The current status of the order (e.g., Created, Accepted, Completed).",
  settlementStatus: "The current settlement status of the order (e.g., TO_BE_INITIATED, PENDING, SETTLED).",
};

const Orders = () => {
  const navigate = useNavigate(); // Used for navigation/viewing order details
  const {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
  } = useOrders(); // Use the new hook



  const handleClearFilters = () => {
    setFilters({
      order_status: "",
      txn_id: "",
      buyer_order_id: "",
      seller_order_id: "",
      buyer_app_id: "",
      seller_app_id: "",
      settlement_status: "",
      domain: "",
      order_created_at_from: "",
      order_created_at_to: "",
      search_by: "",
      search_value: "",
      sort_order: "desc",
    });
  };

  return (
    <>
     {/* {canViewOrders ? ( */}
    <div className="m-4">
    
      <p className="text-muted small mb-3">
        This page provides a consolidated view of all recorded orders. Use the filters below to search for specific orders and click on an Order ID to view detailed information.
      </p>

      {/* --- Filters --- */}
      <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm">
        <div className="row gx-4 gy-3 align-items-end flex-wrap">
          
          {/* Search By */}
          <div className="col-auto">
            <label className="form-label mb-1">Search By</label>
            <div className="input-group" style={{ minWidth: "400px" }}>
              <select
                className="form-select"
                style={{ maxWidth: "150px" }}
                value={filters.search_by}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    search_by: e.target.value,
                    search_value: "",
                  }))
                }
              >
                <option value="">Select Field</option>
                <option value="txn_id">Transaction ID</option>
                <option value="buyer_order_id">Buyer Order ID</option>
                <option value="seller_order_id">Seller Order ID</option>
              </select>

              <input
                type="text"
                className="form-control"
                style={{ fontSize: "12px", padding: "none" }}
                placeholder={`Enter ${
                  filters.search_by
                    ? filters.search_by.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                    : "value"
                }`}
                value={filters.search_value}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search_value: e.target.value }))
                }
                disabled={!filters.search_by}
              />
            </div>
          </div>

          {/* Order Status */}
          <div className="col-auto">
            <label className="form-label mb-1">Order Status</label>
            <select
              className="form-select"
              value={filters.order_status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, order_status: e.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions?.order_statuses?.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          {/* Settlement Status */}
          <div className="col-auto">
            <label className="form-label mb-1">Settlement Status</label>
            <select
              className="form-select"
              value={filters.settlement_status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, settlement_status: e.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions?.settlement_statuses?.map((s) => (
                <option key={s} value={s}>
                  {formatLabel(s)}
                </option>
              ))}
            </select>
          </div>

          {/* Domain */}
          <div className="col-auto">
            <label className="form-label mb-1">Domain</label>
            <select
              className="form-select"
              value={filters.domain}
              onChange={(e) =>
                setFilters((f) => ({ ...f, domain: e.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions?.domains?.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Seller App ID */}
          <div className="col-1">
            <label className="form-label mb-1">Seller App ID</label>
            <select
              className="form-select"
              value={filters.seller_app_id}
              onChange={(e) =>
                setFilters((f) => ({ ...f, seller_app_id: e.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions?.seller_app_ids?.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Buyer App ID */}
          <div className="col-1">
            <label className="form-label mb-1">Buyer App ID</label>
            <select
              className="form-select"
              value={filters.buyer_app_id}
              onChange={(e) =>
                setFilters((f) => ({ ...f, buyer_app_id: e.target.value }))
              }
            >
              <option value="">All</option>
              {filterOptions?.buyer_app_ids?.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Created From */}
          <div className="col-auto">
            <label className="form-label mb-1">Created From</label>
            <input
              type="date"
              className="form-control"
              style={{ fontSize: "12px", padding: "none" }}
              value={filters.order_created_at_from}
              onChange={(e) =>
                setFilters((f) => ({ ...f, order_created_at_from: e.target.value }))
              }
            />
          </div>

          {/* Created To */}
          <div className="col-auto">
            <label className="form-label mb-1">Created To</label>
            <input
              type="date"
              className="form-control"
              style={{ fontSize: "12px", padding: "none" }}
              value={filters.order_created_at_to}
              onChange={(e) =>
                setFilters((f) => ({ ...f, order_created_at_to: e.target.value }))
              }
            />
          </div>

          {/* Clear Filters Button */}
          <div className="col-auto d-flex">
            <button
              type="button"
              className="btn btn-primary no-hover-btn"
              style={{ padding: "5px 8px" }}
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* --- Table and Loader --- */}
      {loading ? (
        <AppLoader />
      ) : (
        <div className="table-responsive mt-4">
          <table className="table table-bordered table-hover align-middle text-center">
            <thead className="table-light text-center">
              <tr>
                <th>
                  Buyer Order ID
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{columnInfo.buyerOrderId}</Tooltip>}
                  >
                    <span style={{ marginLeft: "4px", cursor: "help" }}>
                      <Info size={14} className="text-muted" />
                    </span>
                  </OverlayTrigger>
                </th>
                <th>
                  Seller Order ID
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{columnInfo.sellerOrderId}</Tooltip>}
                  >
                    <span style={{ marginLeft: "4px", cursor: "help" }}>
                      <Info size={14} className="text-muted" />
                    </span>
                  </OverlayTrigger>
                </th>
                <th>
                  Transaction ID
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{columnInfo.txnId}</Tooltip>}
                  >
                    <span style={{ marginLeft: "4px", cursor: "help" }}>
                      <Info size={14} className="text-muted" />
                    </span>
                  </OverlayTrigger>
                </th>
                <th>Domain</th>
                <th>
                  Order Status
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{columnInfo.orderStatus}</Tooltip>}
                  >
                    <span style={{ marginLeft: "4px", cursor: "help" }}>
                      <Info size={14} className="text-muted" />
                    </span>
                  </OverlayTrigger>
                </th>
                <th>
                  Settlement Status
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{columnInfo.settlementStatus}</Tooltip>}
                  >
                    <span style={{ marginLeft: "4px", cursor: "help" }}>
                      <Info size={14} className="text-muted" />
                    </span>
                  </OverlayTrigger>
                </th>
                <th>
                  Total Order Amount
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{columnInfo.totalOrderAmount}</Tooltip>}
                  >
                    <span style={{ marginLeft: "4px", cursor: "help" }}>
                      <Info size={14} className="text-muted" />
                    </span>
                  </OverlayTrigger>
                </th>
                <th>Seller App ID</th>
                <th>Buyer App ID</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      sort_order: f.sort_order === "asc" ? "desc" : "asc",
                      sort_by: "order_created_at" // Sort by created date
                    }))
                  }
                >
                  Order Created At{" "}
                  {filters.sort_by === "order_created_at" && (
                    filters.sort_order === "asc" ? (
                      <span>&uarr;</span>
                    ) : (
                      <span>&darr;</span>
                    )
                  )}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      sort_order: f.sort_order === "asc" ? "desc" : "asc",
                      sort_by: "order_updated_at" // Sort by updated date
                    }))
                  }
                >
                  Order Updated At{" "}
                  {filters.sort_by === "order_updated_at" && (
                    filters.sort_order === "asc" ? (
                      <span>&uarr;</span>
                    ) : (
                      <span>&darr;</span>
                    )
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((order, i) => (
                  <tr key={i}>
                    <td>
                      <div className="copy-cell">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-order-${i}`}>
                              {order.buyer_order_id}
                            </Tooltip>
                          }
                        >
                        <button
                            className="btn btn-link btn-sm p-0 truncate-cell"
                           onClick={() => navigate(`/orders/${order.buyer_order_id}`)}

                          >
                            {order.buyer_order_id}
                          </button>
                        </OverlayTrigger>
                        <CopyIcon textToCopy={order.buyer_order_id} />
                      </div>
                    </td>
                    <td>
                      <div className="copy-cell">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{order.seller_order_id || 'N/A'}</Tooltip>}
                        >
                          <span className="truncate-cell">
                            {order.seller_order_id || 'N/A'}
                          </span>
                        </OverlayTrigger>
                        {order.seller_order_id && <CopyIcon textToCopy={order.seller_order_id} />}
                      </div>
                    </td>
                    <td>
                      <div className="copy-cell">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{order.txn_id}</Tooltip>}
                        >
                          <span className="truncate-cell">
                            {order.txn_id}
                          </span>
                        </OverlayTrigger>
                        <CopyIcon textToCopy={order.txn_id} />
                      </div>
                    </td>
                    <td>{formatLabel(order.domain)}</td>
                    <td>{formatLabel(order.order_status)}</td>
                    <td>{formatLabel(order.settlement_status)}</td>
                    <td>₹{order.total_order_amount?.toFixed(2) || 'N/A'}</td>
                    <td>{order.seller_app_id}</td>
                    <td>{order.buyer_app_id}</td>
                    <td>{order.order_created_at}</td>
                    <td>{order.order_updated_at}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11} className="text-center py-4 text-gray-500">
                    No Orders Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Pagination --- */}
      {totalCount > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          startItem={(currentPage - 1) * itemsPerPage + 1}
          endItem={Math.min(currentPage * itemsPerPage, totalCount)}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
    {/* // ) : (
    //     <UnAuthorized
    //      />
    //   )} */}
    </>
  );
};

export default Orders;